import type { FeedbackContext } from "@/api/feedback-api"

import { SettingsService } from "@/services/settings-service"

import { type BugReportFields, type IncidentFields, type ResponseFeedbackFields } from "./schemas"

export const DEFAULT_RESPONSE_FEEDBACK: ResponseFeedbackFields = {
  sentiment: "down",
  comment: "",
}

const DEFAULT_BUG: BugReportFields = {
  whatTrying: "",
  expected: "",
  happened: "",
  errorText: "",
  reproducibility: "not_sure",
  whoAffected: "only_me",
  whoAffectedOther: "",
  severity: "annoying",
  whenStarted: "",
  pageFeature: "",
  evidenceBlobPath: "",
  screenshotBlobPath: "",
}

export const DEFAULT_INCIDENT: IncidentFields = {
  summary: "",
  impact: "",
  timeframe: "",
  errorText: "",
  evidenceBlobPath: "",
  screenshotBlobPath: "",
}

/**
 * Build a bug-report default that pre-fills the pieces the app already knows:
 * - `whenStarted` — current local time in `datetime-local` format. User can
 *   adjust backwards if the bug actually happened earlier.
 * - `pageFeature` — derived from the current URL path so the user doesn't
 *   have to describe where they are.
 */
export function makeDefaultBug(): BugReportFields {
  const now = new Date()
  const pad = (n: number) => String(n).padStart(2, "0")
  const whenStarted =
    `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}` +
    `T${pad(now.getHours())}:${pad(now.getMinutes())}`
  const pageFeature = typeof window !== "undefined" ? describePage(window.location.pathname) : ""
  return { ...DEFAULT_BUG, whenStarted, pageFeature }
}

/**
 * Human-readable label for the current route. Intentionally strips any
 * sub-path (session IDs, slugs, etc.) — the raw route and session/message
 * IDs are already captured separately in the context, so this field just
 * needs to say *which feature* the user was in.
 */
function describePage(pathname: string): string {
  if (!pathname || pathname === "/") return "Home"
  const first = pathname.split("/").filter(Boolean)[0] ?? ""
  const map: Record<string, string> = {
    explorer: "Explorer chat",
    tender: "Tender",
    parser: "AI Parser",
    translate: "Translator",
    studio: "Studio AI",
    video: "Video Insight",
    projects: "Projects",
    flows: "Flows",
    training: "Training",
    admin: "Admin dashboard",
    memory: "Memory",
    dash: "Dashboards",
  }
  return map[first] ?? first.charAt(0).toUpperCase() + first.slice(1)
}

/**
 * Pull the identity claims the frontend already has cached on the
 * SettingsService. Includes only the fields a support engineer needs to
 * tell *who* is reporting and *what access* they have — no tokens.
 */
export function snapshotIdentity(): FeedbackContext["identity_snapshot"] {
  try {
    const claims = SettingsService.getInstance().claims as Record<string, unknown> | undefined
    if (!claims) return undefined
    const pick = <K extends string>(key: K): unknown => claims[key]
    return {
      oid: (pick("oid") as string | undefined) ?? null,
      tid: (pick("tid") as string | undefined) ?? null,
      name: (pick("name") as string | undefined) ?? null,
      preferred_username: (pick("preferred_username") as string | undefined) ?? null,
      email: (pick("email") as string | undefined) ?? null,
      groups: pick("groups") ?? undefined,
      roles: pick("roles") ?? undefined,
      unique_name: pick("unique_name") ?? undefined,
    }
  } catch {
    return undefined
  }
}
